import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { TextArea } from "../../../../components/TextArea";
import { SelectSingle } from "../../../../components/SelectSingle";
import { InputText } from "../../../../components/InputText";
import { Edit, X, Loader2, PlusCircle, MinusCircle, Users, Calendar, Clock } from "lucide-react";
import { api } from "../../../../services/api";
import moment from "moment";

interface RelatorioTreinamento {
  id: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  treinamento: {
    id: string;
    treinamento: string;
  };
  oficial_responsavel: string;
  criado_em: string;
  patente_treinador: string;
  treinador: {
    id: string;
    nick: string;
  };
  patente_auxiliar: string;
  auxiliar: {
    id: string;
    nick: string;
  };
  sala: string;
  aprovados: any[];
  reprovados: any[];
  qtd_treinados: number;
  qtd_aprovados: number;
  observacao: string;
}

interface Treinado {
  nick: string;
  motivo: string;
  horario: string;
  aprovado: boolean;
}

interface FormData {
  oficial_responsavel: string;
  patente_treinador: string;
  treinador_nick: string;
  patente_auxiliar: string;
  auxiliar_nick: string;
  tipo_relatorio: string;
  id_treinamento: string;
  sala: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  observacao: string;
  treinados: Treinado[];
}

interface EditTreinamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  relatorio: RelatorioTreinamento | null;
  onSuccess: () => void;
}

export function EditTreinamentoModal({
  isOpen,
  onClose,
  relatorio,
  onSuccess
}: EditTreinamentoModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectOficiais, setSelectOficiais] = useState([]);
  const [selectPatentes, setSelectPatentes] = useState([]);
  const [selectTreinamentos, setSelectTreinamentos] = useState([]);

  const [formData, setFormData] = useState<FormData>({
    oficial_responsavel: "",
    patente_treinador: "",
    treinador_nick: "",
    patente_auxiliar: "",
    auxiliar_nick: "",
    tipo_relatorio: "treinamento",
    id_treinamento: "",
    sala: "",
    inicio_treinamento: "",
    termino_treinamento: "",
    observacao: "",
    treinados: [],
  });

  // Carregar dados dos selects
  useEffect(() => {
    if (isOpen) {
      Promise.all([
        api.get("/militares/listOficiais"),
        api.get("/patentes"),
        api.get("/treinamentos")
      ]).then(([oficiaisRes, patentesRes, treinamentosRes]) => {
        setSelectOficiais(oficiaisRes.data);
        setSelectPatentes(patentesRes.data);
        setSelectTreinamentos(treinamentosRes.data);
      }).catch(error => {
        console.error("Erro ao carregar dados:", error);
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (relatorio) {
      // Converter dados do relatório para o formato do formulário
      const treinados: Treinado[] = [
        ...relatorio.aprovados.map(aprovado => ({
          nick: aprovado.nick,
          motivo: "",
          horario: "",
          aprovado: true
        })),
        ...relatorio.reprovados.map(reprovado => ({
          nick: reprovado.nick,
          motivo: reprovado.motivo || "",
          horario: reprovado.horario || "",
          aprovado: false
        }))
      ];

      setFormData({
        oficial_responsavel: relatorio.oficial_responsavel,
        patente_treinador: relatorio.patente_treinador,
        treinador_nick: relatorio.treinador?.nick || "",
        patente_auxiliar: relatorio.patente_auxiliar,
        auxiliar_nick: relatorio.auxiliar?.nick || "",
        tipo_relatorio: "treinamento", // Assumindo padrão
        id_treinamento: relatorio.treinamento.id,
        sala: relatorio.sala,
        inicio_treinamento: moment(relatorio.inicio_treinamento).format("HH:mm"),
        termino_treinamento: moment(relatorio.termino_treinamento).format("HH:mm"),
        observacao: relatorio.observacao || "",
        treinados: treinados.length > 0 ? treinados : [{ nick: "", motivo: "", horario: "", aprovado: false }]
      });
    } else {
      setFormData({
        oficial_responsavel: "",
        patente_treinador: "",
        treinador_nick: "",
        patente_auxiliar: "",
        auxiliar_nick: "",
        tipo_relatorio: "treinamento",
        id_treinamento: "",
        sala: "",
        inicio_treinamento: "",
        termino_treinamento: "",
        observacao: "",
        treinados: [{ nick: "", motivo: "", horario: "", aprovado: false }]
      });
    }
  }, [relatorio, isOpen]);

  const validateForm = (): boolean => {
    // Validações básicas
    if (!formData.oficial_responsavel) {
      enqueueSnackbar("Oficial responsável é obrigatório", { variant: "warning" });
      return false;
    }

    if (!formData.patente_treinador || !formData.treinador_nick) {
      enqueueSnackbar("Patente e nick do treinador são obrigatórios", { variant: "warning" });
      return false;
    }

    if (!formData.patente_auxiliar || !formData.auxiliar_nick) {
      enqueueSnackbar("Patente e nick do auxiliar são obrigatórios", { variant: "warning" });
      return false;
    }

    if (!formData.id_treinamento) {
      enqueueSnackbar("Tipo de treinamento é obrigatório", { variant: "warning" });
      return false;
    }

    if (!formData.sala) {
      enqueueSnackbar("Sala é obrigatória", { variant: "warning" });
      return false;
    }

    if (!formData.inicio_treinamento || !formData.termino_treinamento) {
      enqueueSnackbar("Horários de início e término são obrigatórios", { variant: "warning" });
      return false;
    }

    // Validar treinados
    for (let i = 0; i < formData.treinados.length; i++) {
      const treinado = formData.treinados[i];

      if (!treinado.nick) {
        enqueueSnackbar(`Nick do treinado ${i + 1} é obrigatório`, { variant: "warning" });
        return false;
      }

      if (!treinado.aprovado) {
        if (!treinado.motivo) {
          enqueueSnackbar(`Motivo da reprovação é obrigatório para ${treinado.nick}`, { variant: "warning" });
          return false;
        }
        if (!treinado.horario) {
          enqueueSnackbar(`Horário da saída é obrigatório para ${treinado.nick}`, { variant: "warning" });
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!relatorio || !validateForm()) return;

    try {
      setLoading(true);

      // Extrair apenas o nick do oficial_responsavel (formato: "Patente Nick")
      const extractNickFromOficial = (oficialCompleto: string): string => {
        const texto = oficialCompleto.trim();
        // O nick é sempre a última palavra depois de todos os espaços
        const parts = texto.split(' ');
        return parts[parts.length - 1];
      };

      // Preparar dados para envio
      const dataToSubmit = {
        oficial: extractNickFromOficial(formData.oficial_responsavel),
        treinador: formData.treinador_nick,
        auxiliar: formData.auxiliar_nick,
        patente_treinador: formData.patente_treinador,
        patente_auxiliar: formData.patente_auxiliar,
        tipo_relatorio: formData.tipo_relatorio,
        id_treinamento: formData.id_treinamento,
        sala: formData.sala,
        inicio_treinamento: formData.inicio_treinamento,
        termino_treinamento: formData.termino_treinamento,
        observacao: formData.observacao,
        treinados: formData.treinados,
        status: "P" // Assumindo status padrão
      };

      // Chamada real da API
      await api.put(`/relatorioTreinamento/${relatorio.id}`, dataToSubmit);      enqueueSnackbar("Relatório atualizado com sucesso!", { variant: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar relatório", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      oficial_responsavel: "",
      patente_treinador: "",
      treinador_nick: "",
      patente_auxiliar: "",
      auxiliar_nick: "",
      tipo_relatorio: "treinamento",
      id_treinamento: "",
      sala: "",
      inicio_treinamento: "",
      termino_treinamento: "",
      observacao: "",
      treinados: [{ nick: "", motivo: "", horario: "", aprovado: false }]
    });
    onClose();
  };

  const handleFormChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTreinadoChange = (index: number, field: keyof Treinado, value: any) => {
    const newTreinados = [...formData.treinados];
    newTreinados[index] = { ...newTreinados[index], [field]: value };

    // Limpar campos de reprovação quando aprovado
    if (field === 'aprovado' && value === true) {
      newTreinados[index].motivo = "";
      newTreinados[index].horario = "";
    }

    setFormData(prev => ({ ...prev, treinados: newTreinados }));
  };

  const addTreinado = () => {
    setFormData(prev => ({
      ...prev,
      treinados: [...prev.treinados, { nick: "", motivo: "", horario: "", aprovado: false }]
    }));
  };

  const removeTreinado = (index: number) => {
    if (formData.treinados.length > 1) {
      const newTreinados = formData.treinados.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, treinados: newTreinados }));
    }
  };

  if (!isOpen || !relatorio) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
      <Card className="w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4 gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Edit className="h-5 w-5 text-[oklch(0.45_0.15_142)] shrink-0" />
              Editar Relatório de Treinamento
            </CardTitle>
            <CardDescription className="truncate">
              {relatorio.treinamento.treinamento} — ID: {relatorio.id.substring(0, 8)}...
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0 shrink-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados do Treinamento */}
            <div className="border-l-4 border-l-[oklch(0.45_0.15_142)] p-4 bg-gray-50 dark:bg-gray-800/50 rounded-r-lg">
              <h3 className="text-lg font-semibold text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.75_0.15_142)] flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5" />
                Dados do Treinamento
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <SelectSingle
                    title="Tipo do Relatório"
                    name="tipo_relatorio"
                    value={formData.tipo_relatorio}
                    onChange={(e) => handleFormChange('tipo_relatorio', e.target.value)}
                  >
                    <option value="treinamento">Treinamento</option>
                    <option value="teste">Teste</option>
                  </SelectSingle>
                </div>

                <div>
                  <InputText
                    name="data"
                    title="Data"
                    placeholder="Data"
                    value={moment(relatorio.criado_em).format("DD/MM/YYYY")}
                    disabled
                    readOnly
                  />
                </div>

                <div>
                  <SelectSingle
                    title="Oficial Responsável"
                    name="oficial_responsavel"
                    value={formData.oficial_responsavel}
                    onChange={(e) => handleFormChange('oficial_responsavel', e.target.value)}
                    disabled={true}
                  >
                    <option value="" disabled>
                      Selecione um Oficial
                    </option>
                    {selectOficiais.map(({ nick }) => (
                      <option key={nick} value={nick}>{nick}</option>
                    ))}
                  </SelectSingle>
                </div>

                <div>
                  <SelectSingle
                    title="Patente Treinador"
                    name="patente_treinador"
                    value={formData.patente_treinador}
                    onChange={(e) => handleFormChange('patente_treinador', e.target.value)}
                  >
                    <option value="" disabled>
                      Selecione uma patente
                    </option>
                    {selectPatentes.map(({ patente }) => (
                      <option key={patente} value={patente}>{patente}</option>
                    ))}
                  </SelectSingle>
                </div>

                <div>
                  <InputText
                    name="treinador_nick"
                    title="Nick Treinador"
                    placeholder="Nick"
                    value={formData.treinador_nick}
                    onChange={(e) => handleFormChange('treinador_nick', e.target.value)}
                  />
                </div>

                <div>
                  <SelectSingle
                    title="Patente Auxiliar"
                    name="patente_auxiliar"
                    value={formData.patente_auxiliar}
                    onChange={(e) => handleFormChange('patente_auxiliar', e.target.value)}
                  >
                    <option value="" disabled>
                      Selecione uma patente
                    </option>
                    {selectPatentes.map(({ patente }) => (
                      <option key={patente} value={patente}>{patente}</option>
                    ))}
                  </SelectSingle>
                </div>

                <div>
                  <InputText
                    name="auxiliar_nick"
                    title="Nick Auxiliar"
                    placeholder="Nick"
                    value={formData.auxiliar_nick}
                    onChange={(e) => handleFormChange('auxiliar_nick', e.target.value)}
                  />
                </div>

                <div>
                  <SelectSingle
                    title="Treinamento"
                    name="id_treinamento"
                    value={formData.id_treinamento}
                    onChange={(e) => handleFormChange('id_treinamento', e.target.value)}
                  >
                    <option value="" disabled>
                      Selecione um treinamento
                    </option>
                    {selectTreinamentos.map(({ id, treinamento }) => (
                      <option key={id} value={id}>{treinamento}</option>
                    ))}
                  </SelectSingle>
                </div>

                <div>
                  <SelectSingle
                    title="Sala"
                    name="sala"
                    value={formData.sala}
                    onChange={(e) => handleFormChange('sala', e.target.value)}
                  >
                    <option value="" disabled>
                      Selecione uma sala
                    </option>
                    <option value="1">Sala 1</option>
                    <option value="2">Sala 2</option>
                    <option value="3">Sala 3</option>
                    <option value="4">Sala 4</option>
                    <option value="5">Sala 5</option>
                  </SelectSingle>
                </div>

                <div>
                  <InputText
                    name="inicio_treinamento"
                    title="Início Treinamento"
                    type="time"
                    value={formData.inicio_treinamento}
                    onChange={(e) => handleFormChange('inicio_treinamento', e.target.value)}
                  />
                </div>

                <div>
                  <InputText
                    name="termino_treinamento"
                    title="Término Treinamento"
                    type="time"
                    value={formData.termino_treinamento}
                    onChange={(e) => handleFormChange('termino_treinamento', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Lista de Treinados */}
            <div className="border-l-4 border-l-[oklch(0.45_0.15_142)] p-4 bg-gray-50 dark:bg-gray-800/50 rounded-r-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.75_0.15_142)] flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Lista de Treinados
                </h3>
                <Button
                  type="button"
                  onClick={addTreinado}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              <div className="space-y-4">
                {formData.treinados.map((treinado, index) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputText
                        title="Nick do Treinado"
                        name={`treinado_${index}_nick`}
                        placeholder="Digite o nick"
                        value={treinado.nick}
                        onChange={(e) => handleTreinadoChange(index, 'nick', e.target.value)}
                        required
                      />
                      <div className="flex items-center gap-4">
                        <Label className="dark:text-gray-200">Status:</Label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={treinado.aprovado}
                            onChange={(e) => handleTreinadoChange(index, 'aprovado', e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {treinado.aprovado ? "Aprovado" : "Reprovado"}
                          </span>
                        </label>
                      </div>
                    </div>

                    {!treinado.aprovado && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputText
                          type="time"
                          name={`treinado_${index}_horario`}
                          title="Horário da Saída"
                          value={treinado.horario}
                          onChange={(e) => handleTreinadoChange(index, 'horario', e.target.value)}
                          required
                        />
                        <InputText
                          name={`treinado_${index}_motivo`}
                          title="Motivo da Reprovação"
                          placeholder="Digite o motivo"
                          value={treinado.motivo}
                          onChange={(e) => handleTreinadoChange(index, 'motivo', e.target.value)}
                          required
                        />
                      </div>
                    )}

                    {formData.treinados.length > 1 && (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          onClick={() => removeTreinado(index)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                        >
                          <MinusCircle className="h-4 w-4 mr-1" />
                          Remover
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Observações */}
            <div>
              <Label htmlFor="observacao">Observações Gerais</Label>
              <TextArea
                name="observacao"
                title=""
                value={formData.observacao}
                onChange={(e) => handleFormChange('observacao', e.target.value)}
                placeholder="Digite observações sobre o treinamento"
                className="mt-1"
                disabled={loading}
                rows={4}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar Alterações"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
